import dayjs from "dayjs";

import { QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { RequestQueueItem } from "@/types/requestQueue";
import { checkOPFSWriteSupport } from "@/utils";

const DIR_NAME = "request-queue";
const RETENTION_DAYS = 7;
const SEPARATOR = "&";

// File name format: {userId}&{role}&{YYYY-MM-DD}.json
class OPFSQueueStorage {
    private writePromise: Promise<void> = Promise.resolve();
    private pendingWrite: { key: string; items: RequestQueueItem[] } | null =
        null;

    private sanitize(s: string): string {
        return s.replace(/[^a-zA-Z0-9-]/g, "-");
    }

    // YYYY-MM-DD format allows lexicographic comparison as date comparison
    private getDayKey(date = dayjs()): string {
        return date.format("YYYY-MM-DD");
    }

    private getFileKey(userId: string, role: string, date = dayjs()): string {
        return [
            this.sanitize(userId),
            this.sanitize(role),
            this.getDayKey(date),
        ].join(SEPARATOR);
    }

    private parseFileKey(fileName: string): {
        userId: string;
        role: string;
        date: string;
    } {
        const parts = fileName.replace(".json", "").split(SEPARATOR);
        if (parts.length !== 3)
            throw new Error(`Invalid file name: ${fileName}`);
        return { userId: parts[0], role: parts[1], date: parts[2] };
    }

    private async getDir(): Promise<FileSystemDirectoryHandle> {
        const root = await navigator.storage.getDirectory();
        return root.getDirectoryHandle(DIR_NAME, { create: true });
    }

    private async deleteFile(key: string): Promise<void> {
        try {
            const dir = await this.getDir();
            await dir.removeEntry(`${key}.json`);
        } catch {
            // file may not exist — ignore
        }
    }

    // Queues writes so concurrent calls don't corrupt the file.
    // Only the latest pending write is flushed after the current one finishes.
    private scheduleWrite(key: string, items: RequestQueueItem[]): void {
        this.pendingWrite = { key, items };

        this.writePromise = this.writePromise
            .then(async () => {
                if (this.pendingWrite === null) return;
                const { key, items } = this.pendingWrite;
                this.pendingWrite = null;

                const dir = await this.getDir();
                const file = await dir.getFileHandle(`${key}.json`, {
                    create: true,
                });
                // createWritable writes to a temp file and atomically replaces on close
                const writable = await file.createWritable();
                await writable.write(JSON.stringify(items));
                await writable.close();
            })
            .catch((e) => console.warn("[OPFSQueueStorage] write failed", e));
    }

    // Deletes today's file if queue is empty
    async save(
        userId: string,
        role: string,
        items: RequestQueueItem[],
    ): Promise<void> {
        const key = this.getFileKey(userId, role);

        if (items.length === 0) {
            await this.deleteFile(key);
            return;
        }

        this.scheduleWrite(key, items);
    }

    // Loads non-completed items from past days for the given user+role.
    // Also deletes expired files (any user/role) and consumed files after reading.
    async loadPendingFromPastDays(
        userId: string,
        role: string,
        days = RETENTION_DAYS,
    ): Promise<{ date: string; items: RequestQueueItem[] }[]> {
        try {
            const dir = await this.getDir();
            const cutoff = this.getDayKey(dayjs().subtract(days, "day"));
            const results: { date: string; items: RequestQueueItem[] }[] = [];
            const toDelete: string[] = [];

            for await (const [name] of dir.entries()) {
                let parsed: ReturnType<typeof this.parseFileKey>;

                try {
                    parsed = this.parseFileKey(name);
                } catch {
                    continue;
                }

                // expired — mark for deletion regardless of user/role
                if (parsed.date < cutoff) {
                    toDelete.push(name);
                    continue;
                }

                // skip other users or roles
                if (
                    parsed.userId !== this.sanitize(userId) ||
                    parsed.role !== this.sanitize(role)
                ) {
                    continue;
                }

                try {
                    const file = await dir.getFileHandle(name);
                    const f = await file.getFile();
                    const items: RequestQueueItem[] = JSON.parse(
                        await f.text(),
                    );
                    const pending = items
                        .filter(
                            (item) => item.status !== QueueTaskStatus.Completed,
                        )
                        .map((item) => ({
                            ...item,
                            status: QueueTaskStatus.Idle,
                        }));

                    if (pending.length > 0) {
                        results.push({ date: parsed.date, items: pending });
                        // consumed — will be re-saved under today's key
                        toDelete.push(name);
                    }
                } catch {
                    continue;
                }
            }

            // delete after iteration to avoid mutating the directory mid-loop
            await Promise.all(
                toDelete.map((name) => dir.removeEntry(name).catch(() => null)),
            );

            return results.sort((a, b) => a.date.localeCompare(b.date));
        } catch (error) {
            console.warn(
                "[OPFSQueueStorage] loadPendingFromPastDays failed",
                error,
            );
            return [];
        }
    }

    async cleanup(retentionDays = RETENTION_DAYS): Promise<void> {
        try {
            const dir = await this.getDir();
            const cutoff = this.getDayKey(
                dayjs().subtract(retentionDays, "day"),
            );

            for await (const [name] of dir.entries()) {
                let parsed: ReturnType<typeof this.parseFileKey>;

                try {
                    parsed = this.parseFileKey(name);
                } catch {
                    continue;
                }

                if (parsed.date < cutoff) {
                    await dir.removeEntry(name).catch(() => null);
                }
            }
        } catch (e) {
            console.warn("[OPFSQueueStorage] cleanup failed", e);
        }
    }
}

// No-op fallback for environments where OPFS write is not supported (e.g. iOS Safari)
class NoopQueueStorage {
    async save(): Promise<void> {}
    async loadPendingFromPastDays(): Promise<[]> {
        return [];
    }
    async cleanup(): Promise<void> {}
}

const createOpfsQueueStorage = async (): Promise<
    OPFSQueueStorage | NoopQueueStorage
> => {
    const supported = await checkOPFSWriteSupport();

    console.log("[OPFSQueueStorage]", supported);

    return supported ? new OPFSQueueStorage() : new NoopQueueStorage();
};

let _opfsQueueStorage: OPFSQueueStorage | NoopQueueStorage | null = null;
const _ready = createOpfsQueueStorage().then((s) => {
    _opfsQueueStorage = s;
});

export const opfsQueueStorage = new Proxy(
    {} as OPFSQueueStorage | NoopQueueStorage,
    {
        get(_target, prop: string) {
            return (...args: unknown[]) =>
                _ready.then(() => {
                    const method = _opfsQueueStorage[prop];
                    if (typeof method === "function") {
                        return method.apply(_opfsQueueStorage, args);
                    }
                    return method;
                });
        },
    },
);
